// pages/profile/2factor.jsx
import React, { useState, useEffect } from 'react';
import { useAuth, api } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { 
  FiShield, FiKey, FiCheck, FiX, FiRefreshCw, 
  FiCopy, FiAlertCircle, FiLock,
  FiSmartphone
} from 'react-icons/fi';
import { QRCodeSVG } from 'qrcode.react';

function TwoFactorAuth() {
  const { user, token } = useAuth();
  
  // 2FA States
  const [twoFactor, setTwoFactor] = useState({
    enabled: false,
    verified: false,
    secret: '',
    otpauth_url: '',
    backupCodes: [],
  });
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorPassword, setTwoFactorPassword] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [is2FALoading, setIs2FALoading] = useState(true);
  const [disableConfirm, setDisableConfirm] = useState('');

  useEffect(() => {
    if (token) {
      fetchTwoFactorStatus();
    }
  }, [token]);

  const fetchTwoFactorStatus = async () => {
    setIs2FALoading(true);
    try {
      const response = await api.get('/api/auth/2fa/status');
      if (response.data.success) {
        setTwoFactor({
          ...twoFactor,
          enabled: response.data.enabled || false,
          verified: response.data.verified || false,
        });
      }
    } catch (error) {
      console.error('Error fetching 2FA status:', error);
      if (error.response?.status === 404) {
        setTwoFactor({
          ...twoFactor,
          enabled: false,
          verified: false,
        });
      }
    } finally {
      setIs2FALoading(false);
    }
  };

  const setup2FA = async () => {
    try {
      const response = await api.post('/api/auth/2fa/setup');
      console.log('📦 2FA Setup Response:', response.data);
      
      if (response.data.success) {
        setTwoFactor({
          ...twoFactor,
          secret: response.data.secret,
          otpauth_url: response.data.otpauth_url,
          backupCodes: response.data.backupCodes || [],
        });
        setShow2FASetup(true);
        toast.success('2FA setup initiated! Scan the QR code with your authenticator app.');
      } else {
        toast.error(response.data.error || 'Failed to setup 2FA');
      }
    } catch (error) {
      console.error('Error setting up 2FA:', error);
      toast.error(error.response?.data?.error || 'Failed to setup 2FA');
    }
  };

  const verify2FA = async () => {
    if (!twoFactorCode || twoFactorCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    setVerifyLoading(true);
    try {
      const response = await api.post('/api/auth/2fa/verify', {
        code: twoFactorCode,
        password: twoFactorPassword,
      });
      
      console.log('📦 2FA Verify Response:', response.data);

      if (response.data.success) {
        setTwoFactor({
          ...twoFactor,
          enabled: true,
          verified: true,
        });
        setShow2FASetup(false);
        setTwoFactorCode('');
        setTwoFactorPassword('');
        toast.success('2FA enabled successfully!');
        setShowBackupCodes(true);
      } else {
        toast.error(response.data.error || 'Invalid verification code');
      }
    } catch (error) {
      console.error('Error verifying 2FA:', error);
      toast.error(error.response?.data?.error || 'Failed to verify 2FA');
    } finally {
      setVerifyLoading(false);
    }
  };

  const disable2FA = async () => {
    if (disableConfirm !== 'DISABLE 2FA') {
      toast.error('Please type "DISABLE 2FA" to confirm');
      return;
    }

    const code = prompt('Enter your current 2FA code to disable:');
    if (!code) {
      toast.error('Verification code is required to disable 2FA');
      return;
    }

    try {
      const response = await api.post('/api/auth/2fa/disable', { code });
      if (response.data.success) {
        setTwoFactor({
          ...twoFactor,
          enabled: false,
          verified: false,
          secret: '',
          otpauth_url: '',
          backupCodes: [],
        });
        setDisableConfirm('');
        toast.success('2FA disabled successfully');
      } else {
        toast.error(response.data.error || 'Failed to disable 2FA');
      }
    } catch (error) {
      console.error('Error disabling 2FA:', error);
      toast.error(error.response?.data?.error || 'Failed to disable 2FA');
    }
  };

  const copyBackupCodes = () => {
    const codes = twoFactor.backupCodes.join('\n');
    navigator.clipboard.writeText(codes);
    toast.success('Backup codes copied!');
  };

  const regenerateBackupCodes = async () => {
    try {
      const response = await api.post('/api/auth/2fa/regenerate-codes');
      if (response.data.success) {
        setTwoFactor({
          ...twoFactor,
          backupCodes: response.data.backupCodes || [],
        });
        toast.success('New backup codes generated!');
        setShowBackupCodes(true);
      } else {
        toast.error(response.data.error || 'Failed to regenerate backup codes');
      }
    } catch (error) {
      console.error('Error regenerating backup codes:', error);
      toast.error(error.response?.data?.error || 'Failed to regenerate backup codes');
    }
  };

  if (is2FALoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading 2FA settings...</p>
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ marginBottom: '20px' }}>
        <FiShield style={{ marginRight: '10px' }} />
        Two-Factor Authentication
      </h2>

      {/* Status Card */}
      <div className="card">
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '16px',
          padding: '8px 0',
          flexWrap: 'wrap',
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: twoFactor.enabled ? 'rgba(46, 204, 113, 0.15)' : 'rgba(107, 122, 143, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '28px',
          }}>
            {twoFactor.enabled ? '🔒' : '🔓'}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '18px', fontWeight: '600' }}>
              {twoFactor.enabled ? '2FA is Enabled' : '2FA is Disabled'}
            </div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
              {twoFactor.enabled 
                ? 'Your account is protected with two-factor authentication.'
                : 'Add an extra layer of security to your account.'}
            </div>
          </div>
          <div>
            <span style={{ 
              padding: '4px 16px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '600',
              background: twoFactor.enabled ? 'rgba(46, 204, 113, 0.15)' : 'rgba(107, 122, 143, 0.15)',
              color: twoFactor.enabled ? '#2ecc71' : '#6b7a8f',
            }}>
              {twoFactor.enabled ? '✅ Active' : '⏸️ Inactive'}
            </span>
          </div>
        </div>
      </div>

      {/* Main Action Card */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">
            {twoFactor.enabled ? (
              <><FiShield style={{ color: '#2ecc71' }} /> 2FA Settings</>
            ) : (
              <><FiShield /> Setup 2FA</>
            )}
          </span>
        </div>

        {!twoFactor.enabled ? (
          <div>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: '12px',
              marginBottom: '16px',
            }}>
              <div style={{ 
                background: 'var(--bg-input)', 
                padding: '12px', 
                borderRadius: '8px',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '24px', marginBottom: '4px' }}>📱</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                  Download Authenticator App
                </div>
              </div>
              <div style={{ 
                background: 'var(--bg-input)', 
                padding: '12px', 
                borderRadius: '8px',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '24px', marginBottom: '4px' }}>📸</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                  Scan QR Code
                </div>
              </div>
              <div style={{ 
                background: 'var(--bg-input)', 
                padding: '12px', 
                borderRadius: '8px',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '24px', marginBottom: '4px' }}>🔑</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                  Enter 6-Digit Code
                </div>
              </div>
            </div>
            <button className="btn btn-primary" onClick={setup2FA} style={{ width: '100%' }}>
              <FiShield /> Enable Two-Factor Authentication
            </button>
          </div>
        ) : (
          <div>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr',
              gap: '12px',
              marginBottom: '16px',
            }}>
              <div style={{ 
                background: 'var(--bg-input)', 
                padding: '12px', 
                borderRadius: '8px',
              }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Status</div>
                <div style={{ fontWeight: '600', color: '#2ecc71' }}>✅ Enabled</div>
              </div>
              <div style={{ 
                background: 'var(--bg-input)', 
                padding: '12px', 
                borderRadius: '8px',
              }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Verified</div>
                <div style={{ fontWeight: '600' }}>
                  {twoFactor.verified ? '✅ Yes' : '⏳ Pending'}
                </div>
              </div>
              <div style={{ 
                background: 'var(--bg-input)', 
                padding: '12px', 
                borderRadius: '8px',
              }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Backup Codes</div>
                <div style={{ fontWeight: '600' }}>
                  {twoFactor.backupCodes.length} codes available
                </div>
              </div>
              <div style={{ 
                background: 'var(--bg-input)', 
                padding: '12px', 
                borderRadius: '8px',
              }}>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Security Level</div>
                <div style={{ fontWeight: '600', color: '#2ecc71' }}>🟢 High</div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button 
                className="btn btn-outline" 
                onClick={() => setShowBackupCodes(true)}
                style={{ flex: 1 }}
              >
                <FiKey /> View Backup Codes
              </button>
              <button 
                className="btn btn-outline" 
                onClick={regenerateBackupCodes}
                style={{ flex: 1 }}
              >
                <FiRefreshCw /> Regenerate Codes
              </button>
              <button 
                className="btn btn-danger" 
                onClick={() => document.getElementById('disableSection').scrollIntoView({ behavior: 'smooth' })}
                style={{ flex: 1 }}
              >
                <FiX /> Disable 2FA
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Disable 2FA Section */}
      {twoFactor.enabled && (
        <div className="card" id="disableSection" style={{ borderColor: 'var(--danger)' }}>
          <div className="card-header">
            <span className="card-title" style={{ color: 'var(--danger)' }}>
              <FiAlertCircle /> Disable Two-Factor Authentication
            </span>
          </div>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '12px' }}>
            Disabling 2FA will reduce the security of your account. 
            You will no longer need to enter a verification code when logging in.
          </p>
          <div style={{ 
            background: 'var(--bg-input)', 
            padding: '16px', 
            borderRadius: '8px',
            marginBottom: '12px',
          }}>
            <label style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
              Type <strong style={{ color: 'var(--danger)' }}>DISABLE 2FA</strong> to confirm:
            </label>
            <input
              type="text"
              value={disableConfirm}
              onChange={(e) => setDisableConfirm(e.target.value)}
              placeholder="Type DISABLE 2FA"
              style={{ marginTop: '4px' }}
            />
          </div>
          <button 
            className="btn btn-danger" 
            onClick={disable2FA}
            disabled={disableConfirm !== 'DISABLE 2FA'}
            style={{ width: '100%' }}
          >
            <FiX /> Disable 2FA
          </button>
        </div>
      )}

      {/* 2FA Setup Modal */}
      {show2FASetup && (
        <div className="modal-overlay" onClick={() => setShow2FASetup(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px' }}>
            <div className="modal-header">
              <h3><FiSmartphone /> Setup Two-Factor Authentication</h3>
              <button className="modal-close" onClick={() => setShow2FASetup(false)}>
                <FiX />
              </button>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                <strong>Step 1:</strong> Scan the QR code with your authenticator app
              </p>
              {twoFactor.otpauth_url ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '16px',
                  background: 'var(--bg-input)',
                  borderRadius: '12px',
                  margin: '12px 0',
                  border: '1px solid var(--border-color)',
                }}>
                  <QRCode 
                    value={twoFactor.otpauth_url} 
                    size={200}
                    level="H"
                    includeMargin={true}
                  />
                  <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--text-muted)' }}>
                    Scan this QR code with Google Authenticator or Authy
                  </div>
                </div>
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '16px',
                  background: 'var(--bg-input)',
                  borderRadius: '8px',
                  margin: '12px 0',
                }}>
                  <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
                    📱 Open your authenticator app and add a new account with this secret:
                  </p>
                  <code style={{ 
                    fontFamily: 'monospace', 
                    fontSize: '16px',
                    background: 'var(--bg-card)',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    display: 'inline-block',
                    marginTop: '8px',
                  }}>
                    {twoFactor.secret}
                  </code>
                </div>
              )}
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginTop: '12px' }}>
                <strong>Step 2:</strong> Enter the 6-digit code from your authenticator app
              </p>
            </div>

            <div className="form-group">
              <label>Authentication Code</label>
              <input
                type="text"
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit code"
                maxLength="6"
                autoFocus
                style={{ fontSize: '18px', letterSpacing: '4px', textAlign: 'center' }}
              />
            </div>

            <div className="form-group">
              <label>Confirm Password</label>
              <input
                type="password"
                value={twoFactorPassword}
                onChange={(e) => setTwoFactorPassword(e.target.value)}
                placeholder="Enter your password to confirm"
              />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button 
                className="btn btn-primary" 
                onClick={verify2FA}
                disabled={verifyLoading || !twoFactorCode || twoFactorCode.length !== 6}
                style={{ flex: 1 }}
              >
                {verifyLoading ? 'Verifying...' : 'Verify & Enable'}
              </button>
              <button className="btn btn-outline" onClick={() => setShow2FASetup(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Backup Codes Modal */}
      {showBackupCodes && twoFactor.backupCodes.length > 0 && (
        <div className="modal-overlay" onClick={() => setShowBackupCodes(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h3><FiKey /> Backup Codes</h3>
              <button className="modal-close" onClick={() => setShowBackupCodes(false)}>
                <FiX />
              </button>
            </div>

            <div style={{ 
              background: 'var(--bg-input)', 
              padding: '16px', 
              borderRadius: '8px',
              marginBottom: '16px',
            }}>
              <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '8px' }}>
                <FiAlertCircle style={{ marginRight: '4px', color: 'var(--warning)' }} />
                Save these backup codes in a safe place. Each code can be used once to log in if you lose access to your authenticator app.
              </p>
              <div style={{ 
                fontFamily: 'monospace', 
                fontSize: '14px',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '8px',
              }}>
                {twoFactor.backupCodes.map((code, index) => (
                  <div key={index} style={{ 
                    background: 'var(--bg-card)',
                    padding: '6px 12px',
                    borderRadius: '4px',
                    textAlign: 'center',
                    border: '1px solid var(--border-color)',
                  }}>
                    {code}
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button className="btn btn-primary" onClick={copyBackupCodes} style={{ flex: 1 }}>
                <FiCopy /> Copy Codes
              </button>
              <button className="btn btn-outline" onClick={() => setShowBackupCodes(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
          backdrop-filter: blur(4px);
        }
        
        .modal {
          background: var(--bg-card);
          border-radius: var(--radius);
          padding: 32px;
          max-width: 520px;
          width: 100%;
          border: 1px solid var(--border-color);
          max-height: 90vh;
          overflow-y: auto;
          animation: modalIn 0.2s ease;
        }
        
        @keyframes modalIn {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }
        
        .modal-header h3 {
          margin: 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .modal-close {
          background: none;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          font-size: 20px;
          padding: 4px 8px;
          border-radius: 4px;
          transition: all 0.3s ease;
        }
        
        .modal-close:hover {
          background: var(--bg-hover);
          color: var(--text-primary);
        }
        
        .btn-success {
          background: var(--success);
          color: white;
        }
        
        .btn-success:hover {
          opacity: 0.9;
        }
        
        .btn-danger {
          background: var(--danger);
          color: white;
        }
        
        .btn-danger:hover {
          opacity: 0.9;
        }
        
        .btn-danger:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .modal-2fa-qr {
          background: white;
          padding: 16px;
          border-radius: 12px;
          display: inline-block;
        }
      `}</style>
    </div>
  );
}

export default TwoFactorAuth;